import { useEffect, useState, type FormEvent } from "react";
import type { Profile, ProfileSettings } from "../shared/types";
import { api, passkeyError, registerPasskey, supportsPasskeys } from "./api";
import { playRestAlert, primeRestAlert } from "./audio";
import { getDeviceId } from "./offline";
import { Card, ErrorNotice, Field, SuccessNotice } from "./components";
import { LocationManager } from './Locations';

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
}

export function Settings({
  profile,
  initialSettings,
  onSettingsSaved,
  onProfiles,
}: {
  profile: Profile;
  initialSettings: ProfileSettings;
  onSettingsSaved: (settings: ProfileSettings) => void;
  onProfiles: () => void;
}) {
  const [settings, setSettings] = useState<ProfileSettings>(initialSettings);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [passkeyCount, setPasskeyCount] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    void api<{count:number}>('/auth/passkeys').then(result => { if(active)setPasskeyCount(result.count); }).catch(() => {});
    return () => { active = false; };
  }, [profile.id]);
  useEffect(() => {
    setSettings(initialSettings);
  }, [initialSettings]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/settings", {
        method: "PATCH",
        body: JSON.stringify(settings),
      });
      onSettingsSaved(settings);
      setSaved("Settings saved and applied to workouts and reminders.");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not save settings",
      );
    } finally {
      setBusy(false);
    }
  }
  async function passkey() {
    setBusy(true);
    setError("");
    try {
      await registerPasskey(navigator.platform || "Apple device");
      const status = await api<{count:number}>('/auth/passkeys');
      setPasskeyCount(status.count);
      setSaved("Face ID / passkey registered for this profile on this device.");
    } catch (caught) {
      setError(passkeyError(caught, true));
    } finally {
      setBusy(false);
    }
  }
  async function push() {
    setBusy(true);
    setError("");
    try {
      const standalone =
        matchMedia("(display-mode: standalone)").matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone;
      if (!standalone)
        throw new Error(
          "On iPhone, first open this site in Safari, tap Share, then Add to Home Screen. Open the installed app and enable notifications here.",
        );
      const registration = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== "granted")
        throw new Error(
          "Notifications were not allowed. You can change this in iPhone Settings.",
        );
      const { publicKey } = await api<{ publicKey: string }>(
        "/push/public-key",
      );
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = subscription.toJSON();
      await api("/push/subscribe", {
        method: "POST",
        body: JSON.stringify({
          deviceId: getDeviceId(),
          deviceName: "iPhone Home Screen",
          subscription: { endpoint: json.endpoint, keys: json.keys },
        }),
      });
      setSaved(
        "Notifications are enabled for this profile. Night-before and optional morning reminders use the times below.",
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not enable notifications",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="page-heading">
        <p className="eyebrow">{profile.displayName}</p>
        <h1>Settings</h1>
        <p>
          Timers, schedule, privacy and device features are stored separately
          for this profile.
        </p>
      </header>
      {error && <ErrorNotice message={error} onDismiss={() => setError("")} />}{" "}
      {saved && <SuccessNotice>{saved}</SuccessNotice>}
      <LocationManager profileId={profile.id}/>
      <form onSubmit={save}>
        <Card>
          <h2>Workout</h2>
          <p>
            A 60-second rest runs between sets of the same exercise. After its
            final set, choose any remaining exercise immediately.
          </p>
          <fieldset>
            <legend>Rest alert</legend>
            <div className="radio-stack">
              {[
                ["voice", "Voice"],
                ["soft_chime", "Soft chime"],
                ["off", "Off"],
              ].map(([value, label]) => (
                <label key={value} className="toggle">
                  <input
                    type="radio"
                    name="rest-alert"
                    value={value}
                    checked={settings.alertMode === value}
                    onChange={() =>
                      setSettings({
                        ...settings,
                        alertMode: value as ProfileSettings["alertMode"],
                        chimeEnabled: value !== "off",
                      })
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <p className="muted small">
            Voice plays one gentle chime and says “Time to exercise” once. If
            the recorded voice is unavailable, the chime still plays and stops by itself.
            Alerts request mixing with other audio; any temporary volume dip is controlled by your device.
          </p>
          <button
            type="button"
            className="button button--small"
            disabled={settings.alertMode === "off"}
            onClick={() => {
              void primeRestAlert(settings.alertMode).then(() =>
                playRestAlert(settings.alertMode),
              );
            }}
          >
            Preview alert
          </button>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.keepAwake}
              onChange={(event) =>
                setSettings({ ...settings, keepAwake: event.target.checked })
              }
            />
            <span>Keep screen awake during workouts</span>
          </label>
        </Card>
        <Card>
          <h2>Schedule & reminders</h2>
          <fieldset>
            <legend>Weights days</legend>
            <div className="weekday-picker">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                (day, index) => (
                  <label key={day}>
                    <input
                      type="checkbox"
                      checked={settings.scheduleDays.includes(index)}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          scheduleDays: event.target.checked
                            ? [...settings.scheduleDays, index]
                            : settings.scheduleDays.filter(
                                (value) => value !== index,
                              ),
                        })
                      }
                    />
                    <span>{day}</span>
                  </label>
                ),
              )}
            </div>
          </fieldset>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.reminderEnabled}
              onChange={(event) =>
                setSettings({
                  ...settings,
                  reminderEnabled: event.target.checked,
                })
              }
            />
            <span>Night-before reminder</span>
          </label>
          <Field label="Night-before reminder time">
            <input
              type="time"
              value={settings.reminderTime}
              disabled={!settings.reminderEnabled}
              onChange={(event) =>
                setSettings({ ...settings, reminderTime: event.target.value })
              }
            />
          </Field>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.morningReminder}
              onChange={(event) =>
                setSettings({
                  ...settings,
                  morningReminder: event.target.checked,
                })
              }
            />
            <span>Morning reminder on a workout day</span>
          </label>
          <Field
            label="Morning reminder time"
            hint="Optional and off by default. It names the same uncompleted next workout from your rotation."
          >
            <input
              type="time"
              value={settings.morningReminderTime}
              disabled={!settings.morningReminder}
              onChange={(event) =>
                setSettings({
                  ...settings,
                  morningReminderTime: event.target.value,
                })
              }
            />
          </Field>
          <Field label="Timezone">
            <input
              value={settings.timezone}
              onChange={(event) =>
                setSettings({ ...settings, timezone: event.target.value })
              }
            />
          </Field>
          <p className="muted small">
            On iPhone, add Hard'n'Up to the Home Screen first. Then tap Enable
            iPhone notifications; permission is never requested automatically.
          </p>
          <button
            type="button"
            className="button button--secondary"
            onClick={push}
            disabled={busy}
          >
            Enable iPhone notifications
          </button>
        </Card>
        <Card>
          <h2>Security & profiles</h2>
          <p>{passkeyCount === null ? 'Use a saved passkey to unlock your account with Face ID or your device lock.' : passkeyCount === 0 ? 'Face ID is not set up for this account yet. Save a passkey on this device to enable it.' : `${passkeyCount} saved passkey${passkeyCount === 1 ? '' : 's'} linked to this account. Use Face ID / passkey at sign-in on a device with your saved passkey.`}</p>
          <button
            type="button"
            className="button button--secondary"
            onClick={passkey}
            disabled={busy || !supportsPasskeys()}
          >
            Set up Face ID / passkey
          </button>
          {profile.isAdmin && (
            <button
              type="button"
              className="button button--secondary"
              onClick={onProfiles}
            >
              Manage private profiles
            </button>
          )}
          <p className="muted small">
            Face ID stays on your device. Hard'n'Up stores only the passkey
            public credential, never your face or biometric data.
          </p>
        </Card>
        <button className="button button--primary button--full" disabled={busy}>
          {busy ? "Saving…" : "Save settings"}
        </button>
      </form>
    </>
  );
}
